import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { updatePassword } from '../store/authSlice';

const ChangePasswordScreen = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
    isValid: false
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userInfo, loading, error } = useSelector((state) => state.auth);

  // Password validation function
  const validatePassword = (pwd) => {
    const requirements = [
      { test: pwd.length >= 8, message: 'At least 8 characters long', key: 'length' },
      { test: /[A-Z]/.test(pwd), message: 'At least one uppercase letter (A-Z)', key: 'uppercase' },
      { test: /[a-z]/.test(pwd), message: 'At least one lowercase letter (a-z)', key: 'lowercase' },
      { test: /\d/.test(pwd), message: 'At least one number (0-9)', key: 'number' },
      { test: /[!@#$%^&*(),.?":{}|<>]/.test(pwd), message: 'At least one special character (!@#$%^&*)', key: 'special' },
      { test: pwd.length <= 50, message: 'Maximum 50 characters', key: 'maxLength' },
      { test: !/\s/.test(pwd), message: 'No spaces allowed', key: 'noSpaces' }
    ];

    const passedRequirements = requirements.filter(req => req.test);
    const failedRequirements = requirements.filter(req => !req.test);
    
    const score = (passedRequirements.length / requirements.length) * 100;
    const isValid = failedRequirements.length === 0;
    
    return {
      score: Math.round(score),
      feedback: failedRequirements,
      isValid,
      passedCount: passedRequirements.length,
      totalCount: requirements.length
    };
  };

  // Handle password change with validation
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    if (newPassword) {
      const validation = validatePassword(newPassword);
      setPasswordStrength(validation);
    } else {
      setPasswordStrength({ score: 0, feedback: [], isValid: false });
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
    if (userInfo && !userInfo.mustChangePassword) {
      if (userInfo.role === 'Admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/staff/dashboard');
      }
    } else if (!userInfo) {
      navigate('/login');
    }
  }, [navigate, userInfo, error]);

  const submitHandler = (e) => {
    e.preventDefault();
    
    // Validate password strength
    if (!passwordStrength.isValid) {
      toast.error('Please create a strong password that meets all requirements');
      return;
    }
    
    // Check password confirmation
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    // Additional security checks
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    // Dispatch password update
    dispatch(updatePassword({ password }));
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
      <div className="w-full max-w-lg p-8 space-y-6 bg-white rounded-lg shadow-md">
        <h1 className="text-3xl font-bold text-center text-gray-800">Change Password</h1>
        <p className="text-center text-gray-600">You must change your password to continue.</p>
        
        {/* Password Requirements */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">🔒 Password Requirements</h3>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• At least 8 characters long</li>
            <li>• At least one uppercase letter (A-Z)</li>
            <li>• At least one lowercase letter (a-z)</li>
            <li>• At least one number (0-9)</li>
            <li>• At least one special character (!@#$%^&*)</li>
            <li>• No spaces allowed</li>
          </ul>
        </div>
        
        <form onSubmit={submitHandler} className="space-y-6">
          <div>
            <label
              htmlFor="password"
              className="text-sm font-medium text-gray-700"
            >
              New Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={handlePasswordChange}
              className={`w-full px-4 py-2 mt-2 border rounded-lg focus:ring-2 focus:outline-none ${
                password && !passwordStrength.isValid
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : password && passwordStrength.isValid
                  ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="Enter new password"
              required
            />
            
            {/* Password Strength Indicator */}
            {password && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700">
                    Password Strength: {passwordStrength.passedCount}/{passwordStrength.totalCount}
                  </span>
                  <span className={`text-xs font-medium ${
                    passwordStrength.score >= 100 ? 'text-green-600' :
                    passwordStrength.score >= 70 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {passwordStrength.score >= 100 ? 'Strong' :
                     passwordStrength.score >= 70 ? 'Medium' : 'Weak'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength.score >= 100 ? 'bg-green-500' :
                      passwordStrength.score >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${passwordStrength.score}%` }}
                  ></div>
                </div>
                
                {/* Failed Requirements */}
                {passwordStrength.feedback.length > 0 && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                    <p className="text-xs font-medium text-red-800 mb-1">Missing requirements:</p>
                    <ul className="text-xs text-red-700 space-y-1">
                      {passwordStrength.feedback.map((req, index) => (
                        <li key={index}>• {req.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Success Message */}
                {passwordStrength.isValid && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                    <p className="text-xs font-medium text-green-800">✅ Password meets all security requirements!</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className="text-sm font-medium text-gray-700"
            >
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 mt-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="Confirm new password"
              required
            />
          </div>
          <button
            type="submit"
            disabled={!passwordStrength.isValid || password !== confirmPassword || loading}
            className={`w-full py-3 text-white font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${
              !passwordStrength.isValid || password !== confirmPassword || loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Updating Password...
              </div>
            ) : (
              'Update Password'
            )}
          </button>
          
          {/* Security Notice */}
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Security Notice</h3>
                <p className="text-xs text-yellow-700 mt-1">
                  Your new password will be used for all future logins. Make sure to remember it and keep it secure.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordScreen;

